import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { TabsContent } from "@/components/ui/tabs";
import { Download } from "lucide-react";
import statisticsAPIService, { type MajorQuotaRow } from "@/lib/statistics-api.service";
import { QuotaFillBulletChart } from "./quota-fill-bullet-chart";
import { StatisticsFilterControls, type StatisticsFilters } from "./StatisticsControls";
import { downloadChartPng, toAPIFilter } from "./utils";

interface QuotaTabProps {
	filters?: StatisticsFilters;
	onFiltersChange?: (filters: StatisticsFilters) => void;
	/** Tăng lên mỗi lần trang yêu cầu tải lại; đổi giá trị là fetch lại. */
	refreshToken?: number;
}

/** Tab "Chỉ tiêu": tỉ lệ lấp đầy chỉ tiêu tuyển sinh theo ngành. */
export default function QuotaTab({
	filters = {},
	onFiltersChange,
	refreshToken = 0,
}: QuotaTabProps) {
	const quotaRef = useRef<HTMLDivElement>(null);
	// `undefined` = lần tải đầu chưa xong; refetch giữ dữ liệu cũ, không rơi về
	// skeleton mỗi lần đổi bộ lọc (CLAUDE.md §1).
	const [rows, setRows] = useState<MajorQuotaRow[] | undefined>(undefined);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		statisticsAPIService
			.getMajorQuota(toAPIFilter(filters, "total"))
			.then((data) => {
				if (cancelled) return;
				setRows(data.rows);
				setError(null);
			})
			.catch((err) => {
				if (cancelled) return;
				console.error("Error fetching major quota:", err);
				setError(err instanceof Error ? err.message : "Không tải được số liệu chỉ tiêu ngành");
			});
		return () => {
			cancelled = true;
		};
		// `refreshToken`: nút Tải lại của trang phải làm mới cả tab này.
	}, [filters, refreshToken]);

	return (
		<TabsContent value="quota">
			<div ref={quotaRef} className="space-y-4 md:space-y-6">
				<div className="flex items-center justify-end">
					<Button
						onClick={() => downloadChartPng(quotaRef, "thong-ke-chi-tieu")}
						variant="outline"
						size="sm"
						className="flex items-center gap-2 w-fit"
					>
						<Download className="h-4 w-4" />
						<span className="hidden sm:inline">Tải PNG</span>
						<span className="sm:hidden">PNG</span>
					</Button>
				</div>

				{onFiltersChange && (
					<StatisticsFilterControls
						filterType="total"
						onFilterChange={() => {}}
						chartLimit={10}
						onChartLimitChange={() => {}}
						showChartLimit={false}
						showFilterType={false}
						filters={filters}
						onFiltersChange={onFiltersChange}
						showFilters
					/>
				)}

				{error && (
					<p className="text-sm text-destructive" role="alert">
						{error}
					</p>
				)}

				<QuotaFillBulletChart rows={rows} />
			</div>
		</TabsContent>
	);
}
